import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * Client HTTP unique de la console Admin.
 *
 * Intercepteurs Phase 1 :
 * - Request : injection du Bearer access token (lu depuis un getter).
 * - Response 401 : tentative UNIQUE de refresh ; si succès → retry la requête.
 *   Anti-boucle via `config._retry = true`. Si refresh échoue → callback
 *   `onUnauthorized()` (le store déclenche un logout + redirect /login).
 *
 * Le store Zustand câble les callbacks via `configureAuthClient()` au mount.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1';

/** Marqueur pour anti-boucle de retry. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuth?: boolean;
}

interface AuthAdapter {
  getAccessToken: () => string | null;
  refresh: () => Promise<string | null>;
  onUnauthorized: () => void;
}

let adapter: AuthAdapter = {
  getAccessToken: () => null,
  refresh: async () => null,
  onUnauthorized: () => {
    /* no-op par défaut, remplacé par configureAuthClient */
  },
};

export function configureAuthClient(next: AuthAdapter): void {
  adapter = next;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const cfg = config as RetriableConfig;
  if (cfg._skipAuth === true) {
    return config;
  }
  const token = adapter.getAccessToken();
  if (token !== null && config.headers) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Uploads multipart : retirer le Content-Type (défaut JSON ou en-tête manuel)
  // pour laisser le navigateur poser `multipart/form-data; boundary=…`. Sans ça
  // Django rejette le fichier (« Invalid boundary in multipart »).
  if (config.data instanceof FormData && config.headers) {
    config.headers.delete('Content-Type');
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError) || error.response === undefined) {
      return Promise.reject(error);
    }
    const status = error.response.status;
    const original = error.config as RetriableConfig | undefined;
    if (
      status !== 401 ||
      original === undefined ||
      original._retry === true ||
      original._skipAuth === true
    ) {
      return Promise.reject(error);
    }
    // Une seule tentative de refresh par requête.
    original._retry = true;
    let newAccess: string | null = null;
    try {
      newAccess = await adapter.refresh();
    } catch {
      newAccess = null;
    }
    if (newAccess === null) {
      adapter.onUnauthorized();
      return Promise.reject(error);
    }
    if (original.headers) {
      original.headers.set('Authorization', `Bearer ${newAccess}`);
    }
    return apiClient.request(original);
  },
);

export type ApiRequestConfig = AxiosRequestConfig;

/** Marque une requête comme ne nécessitant pas d'auth (login, refresh). */
export function withoutAuth(config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, _skipAuth: true } as AxiosRequestConfig;
}
